import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Calculator as CalcIcon, AlertTriangle } from "lucide-react";

const CLP = (n) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

export default function Calculator({ onCtaClick }) {
  const [amount, setAmount] = useState([1500000]);
  const [months, setMonths] = useState([12]);
  const rateMonthly = 0.01;

  const { cuota, totalPagar, totalIntereses, cae } = useMemo(() => {
    const P = amount[0];
    const n = months[0];
    const i = rateMonthly;
    const cuota = (P * i) / (1 - Math.pow(1 + i, -n));
    const totalPagar = cuota * n;
    const totalIntereses = totalPagar - P;
    const cae = (Math.pow(1 + i, 12) - 1) * 100;
    return { cuota, totalPagar, totalIntereses, cae };
  }, [amount, months]);

  return (
    <section
      id="calculadora"
      className="py-20 lg:py-28 bg-white"
      data-testid="calculator-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600">
            Calculadora referencial
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Simula tu cuota referencial
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Estos cálculos son solo referenciales y usan una tasa de 1% mensual.
            La tasa, CAE y cuota final las determina la entidad financiera que
            apruebe tu crédito.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-10 w-10 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                <CalcIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">
                  Configura tu simulación
                </div>
                <div className="text-xs text-slate-500">
                  Mueve los controles para ajustar monto y plazo
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="text-sm font-semibold text-slate-700">
                    Monto del crédito
                  </label>
                  <span
                    className="text-xl font-extrabold text-slate-900"
                    data-testid="calc-amount-value"
                  >
                    {CLP(amount[0])}
                  </span>
                </div>
                <Slider
                  value={amount}
                  onValueChange={setAmount}
                  min={500000}
                  max={20000000}
                  step={100000}
                  data-testid="calc-amount-slider"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-2">
                  <span>$500.000</span>
                  <span>$20.000.000</span>
                </div>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <label className="text-sm font-semibold text-slate-700">
                    Plazo (meses)
                  </label>
                  <span
                    className="text-xl font-extrabold text-slate-900"
                    data-testid="calc-months-value"
                  >
                    {months[0]} meses
                  </span>
                </div>
                <Slider
                  value={months}
                  onValueChange={setMonths}
                  min={3}
                  max={60}
                  step={1}
                  data-testid="calc-months-slider"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-2">
                  <span>3 meses (≥61 días)</span>
                  <span>60 meses</span>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-xl bg-amber-50 border border-amber-200 p-4 flex gap-3 items-start">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900 leading-relaxed">
                Simulación referencial basada en una tasa de 1% mensual. La tasa,
                CAE y condiciones finales dependen del perfil del cliente y la
                entidad financiera.
              </p>
            </div>
          </div>

          <div className="lg:col-span-2 rounded-2xl border border-slate-900 bg-slate-900 text-white p-6 sm:p-8 flex flex-col">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal-300">
              Resultado referencial
            </div>

            <div className="mt-6">
              <div className="text-sm text-slate-400">Cuota mensual estimada</div>
              <div
                className="mt-1 text-4xl font-extrabold tracking-tight"
                data-testid="calc-cuota-result"
              >
                {CLP(cuota)}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-slate-800 p-3">
                <div className="text-slate-400 text-xs">Total a pagar</div>
                <div className="font-bold" data-testid="calc-total">
                  {CLP(totalPagar)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-800 p-3">
                <div className="text-slate-400 text-xs">Intereses totales</div>
                <div className="font-bold" data-testid="calc-intereses">
                  {CLP(totalIntereses)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-800 p-3">
                <div className="text-slate-400 text-xs">Tasa mensual</div>
                <div className="font-bold">1,00% (ref.)</div>
              </div>
              <div className="rounded-lg bg-slate-800 p-3">
                <div className="text-slate-400 text-xs">CAE estimada</div>
                <div className="font-bold" data-testid="calc-cae">
                  {cae.toFixed(2)}%
                </div>
              </div>
            </div>

            <Button
              onClick={onCtaClick}
              className="mt-8 w-full bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold rounded-full py-6"
              data-testid="calc-cta-button"
            >
              Solicitar opciones
            </Button>

            <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
              Bancoorp no otorga créditos. Los valores son referenciales y no
              constituyen una oferta. Las condiciones finales son entregadas por
              la entidad financiera.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
